import React, { useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Clock3, Database, Loader2, Sparkles } from "lucide-react";
import SortableTable from "../SortableTable";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

function QueryPlaceholder() {
  return (
    <div className="flex h-full items-center justify-center px-6">
      <div className="max-w-2xl text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-purple-100 text-purple-600 dark:bg-purple-950/50 dark:text-purple-300">
          <Sparkles className="h-8 w-8" />
        </div>
        <h2 className="mt-6 text-2xl font-semibold text-gray-900 dark:text-gray-100">
          Ask your data in natural language
        </h2>
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          Use the AI Query panel on the right to write a question. The active answer will appear here, with enough space for SQL and result exploration.
        </p>
      </div>
    </div>
  );
}

function TableAIQueryResults({ activeEntry }) {
  const [showSqlQuery, setShowSqlQuery] = useState(false);

  const columns = useMemo(() => {
    if (!activeEntry?.response?.results?.length) {
      return [];
    }
    return Object.keys(activeEntry.response.results[0]).map((key) => ({
      key,
      label: key,
      render: (item) => {
        const value = item[key];
        if (value === null) return "NULL";
        if (typeof value === "boolean") return value ? "true" : "false";
        if (typeof value === "object") return JSON.stringify(value);
        return String(value);
      },
      rawValue: (item) => {
        const value = item[key];
        if (value === null) return "";
        if (typeof value === "object") return JSON.stringify(value);
        return String(value);
      },
    }));
  }, [activeEntry]);

  if (!activeEntry) {
    return <QueryPlaceholder />;
  }

  if (activeEntry.status === "loading") {
    return (
      <div className="flex h-full items-center justify-center px-6">
        <div className="flex items-center gap-3 rounded-2xl border border-purple-200 bg-purple-50 px-5 py-4 text-sm text-purple-700 dark:border-purple-900/70 dark:bg-purple-950/40 dark:text-purple-300">
          <Loader2 className="h-5 w-5 animate-spin" />
          Running query: "{activeEntry.query}"
        </div>
      </div>
    );
  }

  if (activeEntry.status === "error") {
    return (
      <div className="p-4">
        <Card className="border-red-200 dark:border-red-900/70">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-300">
              <AlertTriangle className="h-5 w-5" />
              AI Query failed
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
            <p className="rounded-xl bg-gray-50 px-4 py-3 dark:bg-gray-950">{activeEntry.query}</p>
            <p className="rounded-xl bg-red-50 px-4 py-3 text-red-700 dark:bg-red-950/40 dark:text-red-300">
              {activeEntry.error}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const response = activeEntry.response;
  const results = response?.results || [];

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-purple-500 dark:text-purple-300">
                AI Query Result
              </div>
              <CardTitle className="mt-2 text-xl">{activeEntry.query}</CardTitle>
            </div>
            <Button variant="outline" size="small" onClick={() => setShowSqlQuery((current) => !current)}>
              {showSqlQuery ? "Hide SQL" : "Show SQL"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <div className="grid gap-3 md:grid-cols-3">
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-800 dark:bg-gray-950">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <Database className="h-4 w-4" />
                Rows
              </div>
              <div className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {Number(response?.row_count || 0).toLocaleString()}
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-800 dark:bg-gray-950">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <Clock3 className="h-4 w-4" />
                Execution
              </div>
              <div className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {response?.execution_time_ms?.toFixed(1)}ms
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-800 dark:bg-gray-950">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <CheckCircle2 className="h-4 w-4" />
                Status
              </div>
              <div className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">Success</div>
            </div>
          </div>

          {showSqlQuery && (
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-950">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Generated SQL
              </div>
              <pre className="overflow-x-auto whitespace-pre-wrap text-xs text-gray-700 dark:text-gray-300">
                {response?.sql_query}
              </pre>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="min-h-0 flex-1">
        {results.length > 0 ? (
          <SortableTable
            data={results}
            columns={columns}
            emptyMessage="No data returned"
            countLabel={`${response.row_count} result${response.row_count > 1 ? "s" : ""}`}
            exportable={true}
            exportFilename="ai-query-results.csv"
            fullHeight={true}
            className="flex h-full flex-col"
            globalSearch={true}
          />
        ) : (
          <Card className="h-full">
            <CardContent className="flex h-full items-center justify-center">
              <div className="text-center">
                <CheckCircle2 className="mx-auto h-10 w-10 text-green-500" />
                <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Query executed successfully
                </h3>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  This query did not return any rows.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export default TableAIQueryResults;
